import { LotOptionRow } from './lot-option-row'
import type { LotOption } from './lot-items-field'
import { formatQty, formatVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/** Phần tối thiểu của phiếu cân khô cần cho picker nguồn lô phân loại. */
export interface DriedSlipLite {
    id: string
    code: string
    grower_name?: string
    remaining_dry_kg?: number | null
    purchase_date: string
}

/** Dạng dòng nguồn trong form PNK (form giữ tên field nội bộ `source_lot_id`). */
interface ItemWithSources {
    sources?: { source_lot_id?: string; source_quantity?: number }[]
}

/** Giới hạn đã biết của một phiếu: mã + kg còn được lấy trong form này. */
export interface DriedLimit {
    code: string
    available: number
}

function addKg(map: Map<string, number>, id: string | undefined, kg: unknown) {
    if (!id || typeof kg !== 'number' || !Number.isFinite(kg)) return
    map.set(id, (map.get(id) ?? 0) + kg)
}

/** Kg mỗi phiếu khô đã khai ở MỌI dòng khác `exceptItemIdx`. */
export function usedElsewhere(
    items: readonly ItemWithSources[] | undefined,
    exceptItemIdx: number,
): Map<string, number> {
    const out = new Map<string, number>()
    ;(items ?? []).forEach((it, i) => {
        if (i === exceptItemIdx) return
        for (const s of it.sources ?? [])
            addKg(out, s.source_lot_id, s.source_quantity)
    })
    return out
}

/** Kg mỗi phiếu khô mà CHÍNH phiếu đang sửa đã giữ (server đã trừ phần này). */
export function ownUsage(items: readonly ItemWithSources[] | undefined) {
    return usedElsewhere(items, -1)
}

/** Kg còn lấy được từ phiếu `id` cho một dòng — không âm. */
export function availableFor(
    id: string,
    serverRemaining: number,
    ownOriginal: ReadonlyMap<string, number>,
    elsewhere: ReadonlyMap<string, number>,
): number {
    const n =
        serverRemaining + (ownOriginal.get(id) ?? 0) - (elsewhere.get(id) ?? 0)
    return Math.max(0, Math.round(n * 1000) / 1000)
}

/**
 * Nguồn làm tổng kg của một phiếu (cộng mọi dòng) vượt giới hạn. Phiếu chưa
 * biết giới hạn thì bỏ qua — server luôn kiểm lại lúc lưu và lúc duyệt.
 */
export function driedSourceOverLimit(
    items: readonly ItemWithSources[] | undefined,
    limits: ReadonlyMap<string, DriedLimit>,
) {
    const running = new Map<string, number>()
    const out: {
        itemIdx: number
        sourceIdx: number
        code: string
        available: number
    }[] = []
    ;(items ?? []).forEach((it, itemIdx) => {
        ;(it.sources ?? []).forEach((s, sourceIdx) => {
            const id = s.source_lot_id
            const limit = id ? limits.get(id) : undefined
            if (!id || !limit) return
            addKg(running, id, s.source_quantity)
            if ((running.get(id) ?? 0) > limit.available + 1e-9) {
                out.push({
                    itemIdx,
                    sourceIdx,
                    code: limit.code,
                    available: limit.available,
                })
            }
        })
    })
    return out
}

/** Option picker cho một phiếu cân khô, `available` = kg còn lấy được ở dòng. */
export function driedSlipOption(
    d: DriedSlipLite,
    available: number,
): LotOption {
    return {
        value: d.id,
        label: `${d.code} — ${d.grower_name ?? ''}`,
        available,
        unit: 'kg',
        render: () => (
            <LotOptionRow
                title={d.grower_name || d.code}
                code={d.code}
                qty={`${formatQty(available)} kg`}
                createdLine={m.sl_new_source_dried_date({
                    date: formatVN(d.purchase_date),
                })}
            />
        ),
    }
}
